import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wheat, Palette, ArrowRight } from 'lucide-react';
import { trackBannerView, trackBannerClick } from '../../services/bannerTracker';

// IDs estáveis dos banners de vitrine — usados pelo tracker e pelo
// dashboard admin (banner_analytics.summary agrupa por banner_id).
const BANNER_IDS = {
  roca: 'vitrine-produtos-da-roca',
  criativa: 'vitrine-economia-criativa',
};

/**
 * Vitrines Banners — destaques na home do município para
 * "Produtos da Roça" e "Economia Criativa".
 *
 * Props:
 *  - municipioSlug: string usado para montar as URLs absolutas das vitrines.
 *  - municipioId: string (opcional) enviado no tracking para agregação por município.
 *
 * Ambos os cards são clicáveis e navegam para as rotas de vitrine temática
 * já existentes (VitrineTematica). Cliques e views são registrados via
 * `bannerTracker` (endpoint público `/api/banner-analytics/track`).
 */
export default function VitrinesBanners({ municipioSlug, municipioId }) {
  const navigate = useNavigate();

  // Registra views ao montar (dedup por sessão+dia via sessionStorage).
  useEffect(() => {
    if (!municipioSlug) return;
    trackBannerView('vitrine', BANNER_IDS.roca, { municipioId });
    trackBannerView('vitrine', BANNER_IDS.criativa, { municipioId });
  }, [municipioSlug, municipioId]);

  if (!municipioSlug) return null;

  const handleClick = (which) => {
    trackBannerClick('vitrine', BANNER_IDS[which], { municipioId });
    const target = which === 'roca' ? 'produtos-da-roca' : 'economia-criativa';
    navigate(`/${municipioSlug}/${target}`);
  };

  return (
    <section
      className="mt-8"
      data-testid="vitrines-banners"
      aria-label="Vitrines temáticas do município"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Produtos da Roça */}
        <button
          type="button"
          onClick={() => handleClick('roca')}
          className="group relative overflow-hidden rounded-2xl text-left transition-all hover:scale-[1.01] hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-green-500/30"
          data-testid="vitrine-banner-roca"
          style={{
            background:
              'linear-gradient(135deg, #2f7a3e 0%, #4ea955 45%, #a8d566 100%)',
          }}
        >
          {/* Textura sutil */}
          <div
            className="absolute inset-0 opacity-15"
            aria-hidden="true"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 20%, #fff 0.5px, transparent 0.6px), radial-gradient(circle at 80% 60%, #fff 0.6px, transparent 0.7px)',
              backgroundSize: '20px 20px, 26px 26px',
            }}
          />
          <div className="relative p-6 flex items-center gap-4 text-white">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors">
              <Wheat className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-white/80 mb-0.5">
                Vitrine temática
              </p>
              <h3 className="text-lg md:text-xl font-bold leading-tight">
                Conheça os Produtos da Roça
              </h3>
              <p className="text-sm text-white/90 mt-1">
                Agricultores familiares, agroindústria e delícias do campo.
              </p>
            </div>
            <ArrowRight className="w-6 h-6 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>

        {/* Economia Criativa */}
        <button
          type="button"
          onClick={() => handleClick('criativa')}
          className="group relative overflow-hidden rounded-2xl text-left transition-all hover:scale-[1.01] hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-purple-500/30"
          data-testid="vitrine-banner-criativa"
          style={{
            background:
              'linear-gradient(135deg, #56007F 0%, #a24bd6 55%, #FFA52A 100%)',
          }}
        >
          <div
            className="absolute inset-0 opacity-20"
            aria-hidden="true"
            style={{
              backgroundImage:
                'radial-gradient(circle at 60% 30%, #fff 0.7px, transparent 0.8px), radial-gradient(circle at 30% 80%, #fff 0.5px, transparent 0.6px)',
              backgroundSize: '32px 32px, 22px 22px',
            }}
          />
          <div className="relative p-6 flex items-center gap-4 text-white">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors">
              <Palette className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-white/80 mb-0.5">
                Vitrine temática
              </p>
              <h3 className="text-lg md:text-xl font-bold leading-tight">
                Descubra a Economia Criativa
              </h3>
              <p className="text-sm text-white/90 mt-1">
                Artesanato, moda autoral, arte, música e cultura local.
              </p>
            </div>
            <ArrowRight className="w-6 h-6 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>
      </div>
    </section>
  );
}
